import React from "react";
import ProductCard from "./ProductCard";

// Phase 3M — extracted verbatim from RetailCustomer.jsx::MenuSection + EmptyMenu.
// `sectionRef` is the ref-collector callback owned by KioskShell; passing it
// through preserves the IntersectionObserver scroll-spy wiring.
// Customer Favorites (Aug 2026) — `favoritesEnabled` gates whether a star
// renders on a card at all (only when the product carries stable SKU
// identity — decided per-product below); `favoriteAccess` is "locked" for
// a non-member/unverified customer or "live" for an active member;
// `favoritedIds` is the Set of currently-favorited product ids.
export function MenuSection({
  catName, products, currency, itemsInCart, onAdd, onSetQty, onRemove, onInfo, sectionRef,
  // Customer-selectable spice (Sep 2026, restored) — the catalog switch,
  // forwarded to each card unchanged, and the cart indexed by
  // `(product_id, spice_level)` so a card resolves the line matching
  // its OWN current selection.
  spiceEnabled = true, cartLines,
  favoriteAccess, verified = true, favoritedIds, onToggleFavorite, onRequireMembership, onRequireVerification,
  // Sticky Search (Sep 2026) — both optional, so any caller that has
  // not been updated renders exactly as it does today.
  registerProductRef, highlightId,
}) {
  return (
    <section data-cat={catName} ref={sectionRef} className="px-3 sm:px-5 pt-4">
      <div className="bg-emerald-900/90 text-emerald-50 rounded-t-md px-4 py-2 flex items-center justify-between" data-testid={`retail-section-${catName.toLowerCase().replace(/\s+/g, "-")}`}>
        <h2 className="font-serif text-lg">{catName}</h2>
        <span className="text-xs text-emerald-200/70">{products.length} item{products.length === 1 ? "" : "s"}</span>
      </div>
      <div className="bg-[#e8efd0] rounded-b-md p-3 sm:p-4 grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
        {products.map((p) => {
          // `favoriteAccess` itself is omitted entirely by the parent
          // when this tenant has Membership turned off altogether —
          // in that case no star renders anywhere, since there is no
          // way for the star to ever become usable.
          // Returning-member/durable-identity correction (Sep 2026) —
          // a product's own `stable_identity_enabled` (merchant-SKU
          // presence) is NO LONGER a Favorites-eligibility gate: every
          // active product has a durable internal identity (its own
          // `id`) whether or not it carries a merchant SKU — see
          // `services/favorites.py`'s own docstring for the full
          // correction. The prior `"no_sku"` state (a disabled/
          // explained star for a product lacking a merchant SKU) is
          // removed — it described a restriction the backend no
          // longer enforces, and kept manually-created/URL-extracted
          // products (which typically have no merchant SKU at all)
          // permanently unfavoritable. "verify" is shown before OTP
          // verification has happened at all (distinct from "locked",
          // which means verified-but-not-a-member); `verified` defaults
          // to true so a caller that hasn't been updated to pass it
          // explicitly keeps the pre-existing "locked" behavior for
          // every non-"live" case.
          const favoriteState = !favoriteAccess
            ? null
            : !verified
            ? "verify"
            : favoriteAccess !== "live"
            ? "locked"
            : favoritedIds && favoritedIds.has(p.id) ? "filled" : "empty";
          return (
            <ProductCard
              key={p.id}
              product={p}
              currency={currency}
              inCart={itemsInCart.get(p.id)}
              cartLines={cartLines}
              onAdd={(spiceLevel) => onAdd(p.id, spiceLevel)}
              spiceEnabled={spiceEnabled}
              onInc={(item) => onSetQty(item.item_id || item.id, (item.quantity || 0) + 1)}
              onDec={(item) => {
                const next = (item.quantity || 0) - 1;
                if (next <= 0) return onRemove(item.item_id || item.id);
                return onSetQty(item.item_id || item.id, next);
              }}
              onInfo={onInfo}
              cardRef={registerProductRef ? (el) => registerProductRef(p.id, el) : undefined}
              highlight={highlightId === p.id}
              favoriteState={favoriteState}
              onToggleFavorite={onToggleFavorite}
              onRequireMembership={onRequireMembership}
              onRequireVerification={onRequireVerification}
            />
          );
        })}
      </div>
    </section>
  );
}

export function EmptyMenu() {
  return (
    <div className="grid place-items-center py-20 text-stone-300" data-testid="retail-empty-menu">
      <div className="text-center">
        <div className="text-base font-medium text-emerald-100">Menu being prepared</div>
        <p className="text-sm text-emerald-200/60 mt-1">Please check back shortly.</p>
      </div>
    </div>
  );
}

export default MenuSection;
